/**
 * Coverage type validation and helper utilities
 */

/**
 * Extract members from policy quotation
 * @param {Object} policy - The policy object
 * @returns {Array} - Array of members with their benefits and premiums
 */
export const extractMembersFromPolicy = (policy) => {
  if (!policy || !policy.quotation || !policy.quotation.policy_schedule_entries) {
    return [];
  }

  return policy.quotation.policy_schedule_entries.map(entry => {
    // Check if entry has a nested dependant structure or flat structure
    if (entry.dependant) {
      // Nested structure (from policy API response)
      return {
        ...entry.dependant,
        benefits: entry.benefits || [],
        premium_amount_in_cents: entry.premium_amount_in_cents || 0,
        uuid: entry.dependant.additional_details && entry.dependant.additional_details.uuid
          ? entry.dependant.additional_details.uuid
          : entry.dependant.guid,
        type: entry.type || (entry.dependant.additional_details && entry.dependant.additional_details.memberType)
      };
    } else {
      // Flat structure (already in correct format)
      return {
        ...entry,
        uuid: entry.uuid || entry.guid
      };
    }
  });
};

/**
 * Calculate member's age
 * @param {String} dateOfBirth - Date of birth (YYYY-MM-DD or ISO format)
 * @returns {Number} - Age in years
 */
export const calculateAge = (dateOfBirth) => {
  const currentYear = new Date().getFullYear();
  const birthYear = new Date(dateOfBirth).getFullYear();
  return currentYear - birthYear;
};

/**
 * Check if member can be promoted to Principal
 * @param {Object} member - Member object
 * @param {Object} plan - Plan object
 * @returns {Object} - { valid: boolean, reason: string }
 */
export const canBecomePrincipal = (member, plan) => {
  // Children cannot be Principal
  if (member.relationship === 'Child') {
    return {
      valid: false,
      reason: 'Children cannot be Principal'
    };
  }

  // Get Principal age range from plan's nuclear config
  if (!plan || !plan.membership_configs || !plan.membership_configs.content) {
    return { valid: false, reason: 'Plan configuration error' };
  }

  const nuclearConfigs = plan.membership_configs.content.nuclear || [];
  const principalConfig = nuclearConfigs.find(
    n => n.relationship === 'Principal'
  );

  if (!principalConfig || !principalConfig.age_range) {
    return { valid: false, reason: 'Plan configuration error' };
  }

  // Calculate member's age
  const age = calculateAge(member.date_of_birth);
  const { min, max } = principalConfig.age_range;

  if (age < min || age > max) {
    return {
      valid: false,
      reason: `Age ${age} is outside Principal age range (${min}-${max})`
    };
  }

  return { valid: true };
};

/**
 * Get coverage type display name
 * @param {String} coverageType - Coverage type code
 * @returns {String} - Display name
 */
export const getCoverageTypeDisplay = (coverageType) => {
  if (coverageType === 'Member Only' || coverageType === 'Member_Only') {
    return 'Member Only';
  }
  if (coverageType === 'Member & Family' || coverageType === 'Member_And_Family') {
    return 'Member & Family';
  }
  return coverageType;
};

/**
 * Determine amendment direction
 * @param {String} currentType - Current coverage type
 * @param {String} targetType - Target coverage type
 * @returns {String} - 'upgrade' or 'downgrade'
 */
export const getAmendmentDirection = (currentType, targetType) => {
  const currentNormalized = getCoverageTypeDisplay(currentType);
  const targetNormalized = getCoverageTypeDisplay(targetType);

  if (currentNormalized === 'Member & Family' && targetNormalized === 'Member Only') {
    return 'downgrade';
  }
  if (currentNormalized === 'Member Only' && targetNormalized === 'Member & Family') {
    return 'upgrade';
  }
  return null;
};

/**
 * Calculate total premium from benefits
 * @param {Array} benefits - Array of benefit objects
 * @returns {Number} - Total premium in cents
 */
export const calculateMemberTotalPremium = (benefits) => {
  if (!benefits || benefits.length === 0) return 0;

  return benefits.reduce((sum, b) => {
    const premium = b.premium_amount || (b.additional_details && b.additional_details.premium_amount) || 0;
    return sum + premium;
  }, 0);
};
